import React from 'react';
import { AbsoluteFill, spring, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';

interface Word {
  word: string;
  start: number;
  end: number;
}

interface SubtitlesProp {
  captions: Word[];
  width: number;
  height: number;
  isDownload: boolean;
  style: any;
}

export const AnimatedSubtitles: React.FC<SubtitlesProp> = ({ captions, width, height, isDownload, style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (captions === undefined) {
    return <></>;
  }
  const currentTime = frame / fps; // Current time in seconds

  // Find the word that should be displayed at the current time
  const currentWordIndex = captions.findIndex(
    (word) => currentTime >= word.start && currentTime <= word.end
  );
  
  if (currentWordIndex === -1) return <></>;

  // Calculate how many words per line for pagination
  const wordsPerLine = style.wordsPerLine || 5;
  const currentLineStart = Math.floor(currentWordIndex / wordsPerLine) * wordsPerLine;
  
  // Get only the words that should appear on the current line
  const visibleCaptions = captions.slice(
    currentLineStart,
    Math.min(currentLineStart + wordsPerLine, captions.length)
  );

  const animStyle = {
    ...style,
    left: '50%',
    maxWidth: '90%',
    padding: '5px',
    zIndex: 100,
    position: isDownload ? style.position - 300 : style.position
  };

  const scaleFactor = isDownload ? 5 : Math.min(width / 1 / width, height / 1 / height);

  const textShadowSize = style.outlineSize ? `${style.outlineSize}px` : '0px';
  const textShadowColor = style.outlineColor || 'transparent';
  const textShadow = `${textShadowSize} ${textShadowColor}`;

  const adjustedYPosition = style.position;

  // Animation values based on current frame
  const getEntranceAnimation = (word: Word, isCurrent: boolean) => {
    if (!isCurrent || wordsPerLine !== 1) return {};
    
    // Calculate frames based on word timing
    const wordStartFrame = word.start * fps;
    const wordEndFrame = word.end * fps;
    const wordDurationInFrames = wordEndFrame - wordStartFrame;
    
    // Entrance animation (first 20% of duration)
    if (frame >= wordStartFrame && frame <= wordStartFrame + (wordDurationInFrames * 0.2)) {
      const entranceProgress = (frame - wordStartFrame) / (wordDurationInFrames * 0.2);
      const scale = interpolate(
        entranceProgress,
        [0, 0.5, 1],
        [0.5, 1.2, 1],
        {
          extrapolateRight: 'clamp',
          extrapolateLeft: 'clamp'
        }
      );
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'center',
      };
    }
    
    // Exit animation (last 20% of duration)
    if (frame >= wordEndFrame - (wordDurationInFrames * 0.2) && frame <= wordEndFrame) {
      const exitProgress = (frame - (wordEndFrame - wordDurationInFrames * 0.2)) / (wordDurationInFrames * 0.2);
      const scale = interpolate(
        exitProgress,
        [0, 0.5, 1],
        [1, 1.2, 0.5],
        {
          extrapolateRight: 'clamp',
          extrapolateLeft: 'clamp'
        }
      );
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'center',
      };
    }
    
    return {
      transform: 'scale(1)',
      transformOrigin: 'center',
    };
  };

  return (
    <AbsoluteFill>
      <p
        style={{
          position: 'absolute',
          left: '50%',
          transform: `translateX(-50%) ${isDownload ? '' : `scale(${scaleFactor})`} rotate(${style.rotateX}deg)`,
          top: `${adjustedYPosition}px`,
          padding: '10px',
          backgroundColor: style.backgroundColor ?? 'black',
          borderRadius: style.borderRadius,
          color: style.fontColor ?? 'white',
          textAlign: 'center',
          fontFamily: style.font ?? 'Arial',
          fontSize: `${style.fontSize}px`,
          textShadow: textShadow,
          maxWidth: '100%',
          width: style?.whiteSpace ? 'auto' : '80%',
          lineHeight: '1',
          wordSpacing: "1px",
          textTransform: 'uppercase',
          display: style?.whiteSpace && 'inline-block',
          whiteSpace: style?.whiteSpace ?? '',
          boxSizing: style?.whiteSpace && 'border-box',
        }}
      >
        {visibleCaptions?.map((subtitle, index) => {
          const isCurrent = currentTime >= subtitle.start && currentTime <= subtitle.end;
          const isNext = currentTime <= subtitle.end;
          const animationStyles = getEntranceAnimation(subtitle, isCurrent);

          // For debugging
          const isExactlyAtStart = Math.abs(currentTime - subtitle.start) < 0.03;
          
          return (
            <span
              key={`${subtitle.start}-${subtitle.end}-${index}`}
              style={{
                ...animStyle,
                color: isCurrent ? style.highlightTextStyleColor ?? 'yellow' : style.fontColor ?? 'white',
                marginRight: '5px',
                border: isCurrent ? style.highlightTextborder ?? '' : '',
                borderRadius: isCurrent ? style.highlightTextborderRadius ?? '' : '',
                background: isCurrent ? style.highlightTextBackground ?? '' : '',
                display: style.nextWordMystery && (!isCurrent && isNext) ? 'none' : 'inline-block',
                textTransform: 'uppercase',
                ...animationStyles,
                transition: 'color 0.1s ease-out',
              }}
            >
              {subtitle.word}{' '}
            </span>
          );
        })}
      </p>
    </AbsoluteFill>
  );
};

function yPositionAllignmentForVideo(value: number | undefined, playerHeight: number, jsonDataHeight: number) {
  const oldValue = value ?? 50 // Assuming this is the Y position in the original scale

  const oldMin = 0
  const oldMax = jsonDataHeight

  // Define new range
  const newMin = 0
  const newMax = playerHeight

  // Apply formula to scale oldValue from the old range to the new range
  const newValue = Math.round(((oldValue - oldMin) * (newMax - newMin)) / (oldMax - oldMin) + newMin)

  return newValue
}
